import { AlertTriangle, CheckCircle2, Circle, ClipboardCopy, Download, EyeOff, Loader2, MessageSquareWarning, Pencil, Rocket, Send, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { adminClient, type IntegrationDraftManifest, type IntegrationLocale } from '@/api/adminClient';
import { Button } from '@/components/ui/button';
import { LoadError } from '@/components/ui/load-state';
import { Textarea } from '@/components/ui/textarea';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { releaseAudience } from '@/lib/integrationVisibility';
import { cn } from '@/lib/utils';

import { BUILDER_STEPS, LOCALES, copyFor, errorMessage, type BuilderStepId, type StepProps } from '../builderModel';
import { IntegrationCardPreview, StepFrame } from '../builderUi';
import { TestingOrganizationsField, testingOrganizationsInput } from '../TestingOrganizationsField';
import { WhoCanUsePanel } from '../WhoCanUsePanel';

const CHECK_STEP: Record<string, BuilderStepId> = {
  contract: 'contract',
  'base-url': 'contract',
  operations: 'contract',
  capability: 'identity',
  logo: 'identity',
  'copy-en': 'copy',
  'pricing-labels': 'pricing',
  translations: 'copy',
  auth: 'credentials',
  secrets: 'credentials',
  keywords: 'identity',
};

function download(filename: string, contents: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function ReviewStep({ draft, replace, goTo }: StepProps & { goTo: (step: BuilderStepId) => void }) {
  const [manifest, setManifest] = useState<IntegrationDraftManifest>();
  const [locale, setLocale] = useState<IntegrationLocale>('en');
  const [busy, setBusy] = useState<'submit' | 'approve' | 'reject' | 'publish' | 'release' | 'unpublish' | 'reopen'>();
  const [note, setNote] = useState('');
  const { canApproveIntegrations } = useAdminAccess();
  const [manifestFailed, setManifestFailed] = useState(false);
  const [manifestReloadKey, setManifestReloadKey] = useState(0);
  const [testingOrganizationIds, setTestingOrganizationIds] = useState<string[]>(() => draft.testingOrganizationIds ?? []);
  const audience = releaseAudience(draft.visibility);

  useEffect(() => {
    let cancelled = false;
    setManifestFailed(false);
    adminClient
      .getIntegrationDraftManifest(draft.draftId)
      .then((value) => {
        if (!cancelled) setManifest(value);
      })
      .catch(() => {
        if (!cancelled) setManifestFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [draft.draftId, draft.updatedAt, manifestReloadKey]);

  const act = async (kind: 'submit' | 'approve' | 'reject') => {
    setBusy(kind);
    try {
      const next =
        kind === 'submit'
          ? await adminClient.submitIntegrationDraft(draft.draftId)
          : await adminClient.reviewIntegrationDraft(draft.draftId, { decision: kind === 'approve' ? 'approved' : 'rejected', note });
      replace(next);
      setNote('');
      toast.success(kind === 'submit' ? 'Sent for review' : kind === 'approve' ? 'Approved' : 'Changes requested');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(undefined);
    }
  };

  const lifecycle = async (action: 'publish' | 'release' | 'unpublish' | 'reopen') => {
    const prompts = {
      publish: draft.hasRelease
        ? 'Publish this version for its testing organizations? Everyone else keeps the released version until you release this one.'
        : 'Publish it for its testing organizations? Nobody else sees it until you release it.',
      release: `Release this version? It goes to ${audience}, and apps already using the integration switch to it.`,
      unpublish: 'Unpublish it for everyone? It leaves every catalog, and apps that use it show a connect state.',
      reopen:
        draft.status === 'published'
          ? 'Edit it? Its testing organizations keep seeing it while you change it; it has to be submitted and approved again before it is released.'
          : 'Reopen it for changes? It has to be submitted, approved and published again.',
    };
    if (!window.confirm(prompts[action])) return;
    setBusy(action);
    try {
      replace(
        await adminClient.setIntegrationDraftLifecycle(draft.draftId, action, action === 'publish' ? testingOrganizationsInput(testingOrganizationIds) : {}),
      );
      toast.success(
        action === 'publish'
          ? 'Published for its testing organizations'
          : action === 'release'
            ? `Released to ${audience}`
            : action === 'unpublish'
              ? 'Unpublished'
              : 'Editing',
      );
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(undefined);
    }
  };

  const manifestText = manifest ? JSON.stringify(manifest, null, 2) : '';
  // Draft changes to a live integration are submitted, reviewed and applied from the Live / Draft changes panel.
  const lifecycleHere = !draft.hasDraftChanges;
  const blocking = draft.checklist.filter((check) => !check.ok);
  const previewCopy = copyFor(draft, locale).title ? copyFor(draft, locale) : copyFor(draft, 'en');

  return (
    <StepFrame
      title="Review & submit"
      description="Check that everything is in place, see how the integration reads in each language, and send it for review. An integration approver approves it, publishes it for its testing organizations, then releases it to whoever can use it."
    >
      {draft.hasDraftChanges ? (
        <div className="rounded-xl border border-border bg-card/40 p-4 text-sm text-muted-foreground">
          These are draft changes to a live integration. Submit them, review them and apply them from the Draft changes panel at the top of the page.
        </div>
      ) : null}
      {lifecycleHere && draft.status === 'rejected' && draft.review ? (
        <div className="flex gap-3 rounded-xl border border-warning/30 bg-warning-bg/40 p-4">
          <MessageSquareWarning className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <div className="text-sm">
            <div className="font-medium">Changes requested by {draft.review.reviewedBy}</div>
            <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{draft.review.note}</p>
          </div>
        </div>
      ) : null}
      {lifecycleHere && draft.status === 'approved' ? (
        <div className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-success/30 bg-success-bg/40 p-5">
          <div className="flex gap-3">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" />
            <div className="text-sm">
              <div className="font-medium">Approved{draft.review ? ` by ${draft.review.reviewedBy}` : ''} — ready to publish</div>
              {draft.review?.note ? <p className="mt-1 whitespace-pre-wrap text-foreground/90">“{draft.review.note}”</p> : null}
              <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-muted-foreground">
                <li>Publishing makes it live for its testing organizations. Everyone else who can use it gets it only when you release it.</li>
                <li>It is on in every organization that can use it unless they turn it off; prompts containing its keywords then pin it automatically.</li>
                <li>
                  Generated apps call it with <code className="font-mono">ctx.integrations.require(&apos;{draft.capability}&apos;)</code>; Studio holds
                  the credentials and meters every call.
                </li>
              </ul>
              {canApproveIntegrations ? (
                <div className="mt-4 max-w-md">
                  <TestingOrganizationsField value={testingOrganizationIds} onChange={setTestingOrganizationIds} disabled={Boolean(busy)} />
                </div>
              ) : null}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => void lifecycle('reopen')} disabled={Boolean(busy)}>
              <Pencil className="mr-2 h-4 w-4" />
              Reopen
            </Button>
            {canApproveIntegrations ? (
              <Button onClick={() => void lifecycle('publish')} disabled={Boolean(busy)}>
                {busy === 'publish' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Rocket className="mr-2 h-4 w-4" />}
                Publish for testing
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      {lifecycleHere && draft.status === 'published' ? (
        <div className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-success/30 bg-success-bg/40 p-5">
          <div className="flex gap-3">
            <Rocket className="mt-0.5 h-4 w-4 shrink-0 text-success" />
            <div className="text-sm">
              <div className="font-medium">
                {draft.audience === 'internal'
                  ? 'Live for its testing organizations only'
                  : `Live for ${audience}${draft.releasedBy ? ` — released by ${draft.releasedBy}` : ''}`}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {draft.audience === 'internal'
                  ? draft.hasRelease
                    ? 'Its testing organizations are trying this version in their apps. Everyone else keeps the released version until you release this one.'
                    : 'Its testing organizations can build with it in their apps. Nobody else sees it until you release it.'
                  : `In the catalog of ${audience} and callable by generated apps. To change it, select Edit at the top of the page; customers keep this version until your changes are approved and applied.`}{' '}
                Stored credentials can be rotated at any time on the Credentials step.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {draft.hasRelease ? null : (
              <Button variant="outline" onClick={() => void lifecycle('reopen')} disabled={Boolean(busy)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
            {canApproveIntegrations ? (
              <Button variant="outline" onClick={() => void lifecycle('unpublish')} disabled={Boolean(busy)}>
                {busy === 'unpublish' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <EyeOff className="mr-2 h-4 w-4" />}
                Unpublish
              </Button>
            ) : null}
            {canApproveIntegrations && draft.audience === 'internal' ? (
              <Button onClick={() => void lifecycle('release')} disabled={Boolean(busy)}>
                {busy === 'release' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Rocket className="mr-2 h-4 w-4" />}
                Release to whoever can use it
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="overflow-hidden rounded-xl border border-border">
          <div className="flex items-center justify-between bg-card/40 px-5 py-3">
            <div className="text-sm font-semibold">Checklist</div>
            <div className="text-xs text-muted-foreground">
              {draft.checklist.length - blocking.length}/{draft.checklist.length} complete
            </div>
          </div>
          <ul>
            {draft.checklist.map((check) => (
              <li key={check.id} className="flex items-start gap-3 border-t border-border px-5 py-3">
                {check.warning ? (
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                ) : check.ok ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                ) : (
                  <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm">{check.label}</div>
                  {check.detail ? <div className="text-xs text-muted-foreground">{check.detail}</div> : null}
                </div>
                {(!check.ok || check.warning) && CHECK_STEP[check.id] ? (
                  <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => goTo(CHECK_STEP[check.id])}>
                    {BUILDER_STEPS.find((step) => step.id === CHECK_STEP[check.id])?.label}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1">
            {LOCALES.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setLocale(item.id)}
                className={cn(
                  'rounded-md px-2 py-1 text-[11px] transition-colors',
                  locale === item.id ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
                  !draft.copy[item.id]?.title && item.id !== 'en' && 'opacity-60',
                )}
              >
                {item.native}
              </button>
            ))}
          </div>
          <IntegrationCardPreview
            copy={previewCopy}
            category={draft.category}
            brandColor={draft.brandColor}
            logoDataUrl={draft.logoDataUrl}
            pricing={draft.pricing}
          />
          {locale !== 'en' && !draft.copy[locale]?.title ? (
            <p className="text-[11px] text-muted-foreground">Not translated — this language shows the English copy.</p>
          ) : null}
        </div>
      </div>

      <WhoCanUsePanel draft={draft} />

      <div className="overflow-hidden rounded-xl border border-border">
        <div className="flex flex-wrap items-center justify-between gap-3 bg-card/40 px-5 py-3">
          <div>
            <div className="text-sm font-semibold">Package manifest</div>
            <div className="text-xs text-muted-foreground">
              The same shape an SDK integration ships: <code className="font-mono">studio-integration.json</code> plus one copy file
              per language. It holds credential names, never values.
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!manifest}
              onClick={() => void navigator.clipboard.writeText(manifestText).then(() => toast.success('Copied'))}
            >
              <ClipboardCopy className="mr-2 h-3.5 w-3.5" />
              Copy
            </Button>
            <Button variant="outline" size="sm" disabled={!manifest} onClick={() => download(`${draft.integrationId}.studio-integration.json`, manifestText)}>
              <Download className="mr-2 h-3.5 w-3.5" />
              Download
            </Button>
          </div>
        </div>
        {!manifest && manifestFailed ? (
          <LoadError
            compact
            className="border-t border-border bg-background px-5 py-4"
            onRetry={() => setManifestReloadKey((key) => key + 1)}
          />
        ) : (
          <pre className="max-h-96 overflow-auto border-t border-border bg-background p-5 font-mono text-[11px] leading-5 text-muted-foreground">
            {manifestText || 'Loading…'}
          </pre>
        )}
      </div>

      {lifecycleHere && (draft.status === 'draft' || draft.status === 'rejected') ? (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card/40 p-5">
          <div className="text-sm">
            {draft.readyForReview ? (
              <span>Everything required is in place.</span>
            ) : (
              <span className="text-muted-foreground">
                {blocking.length} item{blocking.length === 1 ? '' : 's'} left before this can go to review.
              </span>
            )}
          </div>
          <Button onClick={() => void act('submit')} disabled={!draft.readyForReview || Boolean(busy)}>
            {busy === 'submit' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Submit for review
          </Button>
        </div>
      ) : null}

      {lifecycleHere && draft.status === 'in_review' ? (
        <div className="space-y-3 rounded-xl border border-brand/30 bg-brand-muted/20 p-5">
          <div className="text-sm font-semibold">Review</div>
          <p className="text-xs leading-5 text-muted-foreground">
            Submitted by {draft.submittedBy ?? draft.updatedBy}. Check write operations for idempotency, browser-safe credentials,
            pricing against the vendor's price list, and that the agent guidance keeps server credentials in the App Service.
            {canApproveIntegrations ? null : ' An integration approver approves it or requests changes.'}
          </p>
          {canApproveIntegrations ? (
            <>
              <Textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="Notes for the author (required to request changes)" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => void act('reject')} disabled={!note.trim() || Boolean(busy)}>
                  {busy === 'reject' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Request changes
                </Button>
                <Button onClick={() => void act('approve')} disabled={Boolean(busy)}>
                  {busy === 'approve' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                  Approve
                </Button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </StepFrame>
  );
}
